import React, { useState } from 'react';
import { LinkIcon, TagIcon, CalendarIcon, TrashIcon, ChevronDownIcon, ChevronUpIcon, PencilIcon, ArrowPathIcon, FolderPlusIcon, CheckIcon, BookmarkIcon as BookmarkOutlineIcon, DocumentTextIcon, VideoCameraIcon } from '@heroicons/react/24/outline';
import { BookmarkIcon as BookmarkSolidIcon } from '@heroicons/react/24/solid';
import EditBookmarkModal from './EditBookmarkModal';
import AddToCollectionModal from './AddToCollectionModal';

const MEDIA_TYPE_CONFIG = {
  pdf:   { label: 'PDF',   Icon: DocumentTextIcon, className: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400' },
  video: { label: 'Video', Icon: VideoCameraIcon,  className: 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-400' },
};

const MediaTypePill = ({ mediaType }) => {
  const config = MEDIA_TYPE_CONFIG[mediaType];
  if (!config) return null;
  const { label, Icon, className } = config;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ${className}`}>
      <Icon className="h-3 w-3" />
      {label}
    </span>
  );
};

const BookmarkCard = ({ bookmark, onDelete, onUpdate, onRetry, highlighted = false, selected = false, selectionActive = false, onSelect }) => {
  const [showAllTags, setShowAllTags] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCollectionModalOpen, setIsCollectionModalOpen] = useState(false);

  const handleEditSave = async (updatedData) => {
    if (onUpdate) {
      await onUpdate(bookmark.bookmarkId, updatedData);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'processed':
        return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400';
      case 'processing':
      case 'content_fetched':
      case 'metadata_extracted':
      case 'analyzed':
        return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400';
      case 'failed':
        return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
    }
  };

  const renderTags = () => {
    if (!bookmark.tags || bookmark.tags.length === 0) return null;

    const tagsToShow = showAllTags ? bookmark.tags : bookmark.tags.slice(0, 3);
    const hasMoreTags = bookmark.tags.length > 3;

    return (
      <div className="flex items-start space-x-1">
        <TagIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
        <div className="flex flex-wrap gap-1">
          {tagsToShow.map((tag, index) => (
            <span key={index} className="bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300 px-2 py-1 rounded-full text-xs font-medium">
              {tag}
            </span>
          ))}
          {hasMoreTags && (
            <button
              onClick={() => setShowAllTags(!showAllTags)}
              className="flex items-center space-x-1 text-xs text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-200 px-2 py-1 rounded-full hover:bg-primary-50 dark:hover:bg-primary-900/30 transition-colors"
            >
              <span>
                {showAllTags ? 'Show less' : `+${bookmark.tags.length - 3} more`}
              </span>
              {showAllTags ? (
                <ChevronUpIcon className="h-3 w-3" />
              ) : (
                <ChevronDownIcon className="h-3 w-3" />
              )}
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      id={`bookmark-${bookmark.bookmarkId}`}
      className={`bookmark-card relative transition-all duration-300 group
        ${highlighted ? 'ring-2 ring-primary-400' : ''}
        ${selected ? 'ring-2 ring-primary-500 !bg-primary-50 dark:!bg-primary-900/40' : ''}
      `}
    >
      {/* Selection checkbox — top-left, visible on hover or when selection is active */}
      {onSelect && (
        <button
          onClick={(e) => { e.stopPropagation(); onSelect(bookmark.bookmarkId); }}
          className={`absolute top-2 left-2 w-5 h-5 rounded border-2 flex items-center justify-center transition-all z-10
            ${selected ? 'bg-primary-600 border-primary-600' : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 hover:border-primary-400'}
            ${selectionActive || selected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}
          `}
          aria-label={selected ? 'Deselect bookmark' : 'Select bookmark'}
        >
          {selected && <CheckIcon className="h-3 w-3 text-white" />}
        </button>
      )}

      {/* Status + actions — small, pinned top-right */}
      <div className="absolute top-3 right-3 flex items-center gap-0.5">
        <span className={`px-1 py-0.5 rounded-full text-[10px] font-medium leading-none ${getStatusColor(bookmark.status)}`}>
          {bookmark.status || 'pending'}
        </span>
        <button
          onClick={() => onUpdate && onUpdate(bookmark.bookmarkId, { readLater: !bookmark.readLater })}
          className={`p-0.5 transition-colors ${bookmark.readLater ? 'text-amber-500 hover:text-amber-600' : 'text-gray-300 hover:text-amber-400'}`}
          title={bookmark.readLater ? 'Remove from reading list' : 'Save to reading list'}
        >
          {bookmark.readLater
            ? <BookmarkSolidIcon className="h-3 w-3" />
            : <BookmarkOutlineIcon className="h-3 w-3" />
          }
        </button>
        <button
          onClick={() => setIsCollectionModalOpen(true)}
          className="p-0.5 text-gray-300 hover:text-primary-500 transition-colors"
          title="Add to collection"
        >
          <FolderPlusIcon className="h-3 w-3" />
        </button>
        <button
          onClick={() => setIsEditModalOpen(true)}
          className="p-0.5 text-gray-300 hover:text-primary-500 transition-colors"
          title="Edit"
        >
          <PencilIcon className="h-3 w-3" />
        </button>
        <button
          onClick={() => onDelete(bookmark.bookmarkId)}
          className="p-0.5 text-gray-300 hover:text-red-500 transition-colors"
          title="Delete"
        >
          <TrashIcon className="h-3 w-3" />
        </button>
      </div>

      {/* Title — full card width */}
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
        {bookmark.title || 'Untitled'}
      </h3>

      {/* URL */}
      <div className="mb-3 flex items-center gap-2 min-w-0">
        <a
          href={bookmark.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm min-w-0"
        >
          <LinkIcon className="h-4 w-4 mr-1 flex-shrink-0" />
          <span className="truncate">{bookmark.url}</span>
        </a>
        <MediaTypePill mediaType={bookmark.mediaType} />
      </div>

      {/* Error detail for failed bookmarks */}
      {bookmark.status === 'failed' && (
        <div className="mb-3 flex items-start gap-2 rounded-md bg-red-50 dark:bg-red-900/30 border border-red-100 dark:border-red-800 px-3 py-2 text-xs text-red-700 dark:text-red-400">
          <span className="flex-1">
            {bookmark.failureType === 'dead_link'
              ? 'This link is no longer available (page not found or domain gone).'
              : bookmark.failureType === 'content_too_large'
                ? 'The content at this link is too large to process.'
                : bookmark.errorMessage
                  ? `Could not process: ${bookmark.errorMessage}`
                  : 'Processing failed. You can retry to try again.'}
          </span>
        </div>
      )}

      {bookmark.description && (
        <p className="text-gray-600 dark:text-gray-400 text-sm mb-3 line-clamp-2">
          {bookmark.description}
        </p>
      )}

      {bookmark.summary && (
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-md p-3 mb-3">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            {bookmark.summary}
          </p>
        </div>
      )}

      {bookmark.notes && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border-l-2 border-amber-300 dark:border-amber-700 rounded-r-md px-3 py-2 mb-3">
          <p className="text-xs font-medium text-amber-700 dark:text-amber-400 mb-0.5">Notes</p>
          <p className="text-sm text-amber-900 dark:text-amber-300 line-clamp-3">{bookmark.notes}</p>
        </div>
      )}

      <div className="flex flex-col space-y-3">
        {/* Tags Section */}
        {renderTags()}

        {/* Categories Section */}
        {bookmark.categories && bookmark.categories.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {bookmark.categories.map((cat, index) => (
              <span key={index} className="bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 px-2 py-1 rounded-full text-xs font-medium">
                {cat}
              </span>
            ))}
          </div>
        )}

        {/* Date and Retry */}
        <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
          {['failed', 'processed', 'processing'].includes(bookmark.status) && onRetry && (
            <button
              onClick={() => onRetry(bookmark.bookmarkId)}
              className={`flex items-center space-x-1 text-xs px-2 py-1 rounded transition-colors ${
                bookmark.status === 'processed'
                  ? 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                  : bookmark.failureType === 'dead_link'
                    ? 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    : 'text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30'
              }`}
              title={bookmark.failureType === 'dead_link' ? 'Link may be permanently gone, but you can retry' : undefined}
            >
              <ArrowPathIcon className="h-3 w-3" />
              <span>{bookmark.status === 'processed' ? 'Reprocess' : bookmark.failureType === 'dead_link' ? 'Retry anyway' : 'Retry'}</span>
            </button>
          )}
          <div className="flex items-center space-x-1 ml-auto">
            <CalendarIcon className="h-4 w-4" />
            <span>{formatDate(bookmark.createdAt)}</span>
          </div>
        </div>
      </div>

      <EditBookmarkModal
        bookmark={bookmark}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleEditSave}
      />
      <AddToCollectionModal
        bookmarkId={bookmark.bookmarkId}
        isOpen={isCollectionModalOpen}
        onClose={() => setIsCollectionModalOpen(false)}
      />
    </div>
  );
};

export default BookmarkCard;
